import { AfterViewInit, Component, ElementRef, Injector, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import {
  CHANNELS_ENUMERATION,
  CHANNELS_EVENTS_ENUMERATION,
  CustomerRecurrenceService,
  MarketingEventEnumeration,
  MarketingEventService,
} from '@shared/services';
import { InsuranceNotificationService } from '../../insurance-notification';

// Environment
import { environment } from '@environments/environment';

// Configs
import { COOKIE_ENUMERATION } from '@home/login/configs';
import { ROUTES_CONSTANTS } from '@shared/configs';

// Helpers
import { RFRedirectionHandler, RPRedirectionHandler, ThirdPartyRedirectionHandler } from './helpers';

// Types
import { LoanRedirectOptionsType, RedirectionHandlerType } from './models';
import { ProductType } from '@core/model/enums';

@Component({
  selector: 'app-unified-home',
  templateUrl: './unified-home.component.html',
})
export class UnifiedHomeComponent implements AfterViewInit, OnDestroy {
  @ViewChild('unifiedHomeRef') public unifiedHomeRef: ElementRef;
  public unifiedHomeRefInstance: { build: Function; destroy: Function };

  private readonly clientId: string;

  private readonly authService: AuthService;
  private readonly customerRecurrenceService: CustomerRecurrenceService;
  private readonly webElementService: WebcomponentsService;
  private readonly router: Router;
  private readonly cookieService: CookieService;
  private readonly ngZone: NgZone;
  private readonly marketingEventService: MarketingEventService;
  private readonly dialog: MatDialog;
  private readonly insuranceNotificationService: InsuranceNotificationService;

  private redirectionHandlers: RedirectionHandlerType[];

  constructor(private readonly injector: Injector) {
    this.authService = this.injector.get(AuthService);
    this.customerRecurrenceService = this.injector.get(CustomerRecurrenceService);
    this.webElementService = this.injector.get(WebcomponentsService);
    this.router = this.injector.get(Router);
    this.cookieService = this.injector.get(CookieService);
    this.ngZone = this.injector.get(NgZone);
    this.marketingEventService = this.injector.get(MarketingEventService);
    this.dialog = this.injector.get(MatDialog);
    this.insuranceNotificationService = this.injector.get(InsuranceNotificationService);
    this.clientId = this.authService.getTokenData(this.authService.token)?.clienteId?.toString() || '';

    this.redirectionHandlers = [
      new RFRedirectionHandler(this.injector),
      new ThirdPartyRedirectionHandler(this.injector),
      new RPRedirectionHandler(this.injector),
    ];
  }

  public ngOnDestroy(): void {
    this.unifiedHomeRefInstance?.destroy();
  }

  public ngAfterViewInit(): void {
    this.marketingEventService.notifyEventToChannel(CHANNELS_ENUMERATION.CLOSE_CHANNEL, {
      event: CHANNELS_EVENTS_ENUMERATION.HOME,
      payload: { token: this.authService.token },
    });
    this.loadUnifiedHomeRef();
  }

  public loadUnifiedHomeRef(): void {
    this.webElementService.isLoaded$.pipe(filter(Boolean), take(1)).subscribe({
      next: () => {
        this.unifiedHomeRefInstance = this.unifiedHomeRef.nativeElement._ngElementStrategy?.componentRef.instance;
        this.unifiedHomeRefInstance.build({
          loansRedirectFn: this.loansRedirect.bind(this),
          redirectToExtendTermFn: this.redirectToExtendTerm.bind(this),
          redirectToPaymentFn: this.redirectToPayment.bind(this),
          referralsRedirectionFn: this.redirectToReferrals.bind(this),
          redirectToAlternativeProductSimulatorFn: this.redirectToAlternativeProductSimulator.bind(this),
          redirectToProductCampaignFn: this.redirectToProductCampaign.bind(this),
          customerType: this.customerRecurrenceService.getRecurrenceStatus(),
        });
        if (this.clientId) {
          this.checkInsuranceNotification();
        }
      },
    });
  }

  private loansRedirect(options: LoanRedirectOptionsType): void {
    const handlerStrategy = this.redirectionHandlers.find((h) => h.canHandle(options));
    if (handlerStrategy) {
      handlerStrategy.handle(options);
    }
  }

  private redirectToExtendTerm(event: { novationTypeId: string; loanCode: number }): void {
    this.ngZone.run(() => {
      const urlToRedirect =
        event.novationTypeId === environment.novationTypeId.RNE
          ? ROUTES_CONSTANTS.RENEGOTIATE_LOAN
          : ROUTES_CONSTANTS.EXTEND_TERM;
      this.router.navigate([urlToRedirect, event.loanCode], {
        queryParams: { novationTypeId: event.novationTypeId },
      });
    });
  }

  private redirectToPayment(event: { novationTypeId: string; loanCode: number }): void {
    this.ngZone.run(() => {
      this.router.navigate([ROUTES_CONSTANTS.VALUE_TO_PAY, event.loanCode], {
        queryParams: { novationTypeId: event.novationTypeId },
      });
    });
  }

  private redirectToAlternativeProductSimulator({ alternativeProductName }: { alternativeProductName: string }): void {
    this.ngZone.run(() => {
      if (alternativeProductName === ProductType.RFYA) {
        this.marketingEventService.sendMarketingEvents(MarketingEventEnumeration.LOAN_APPLICATION_RFYA);
        this.router.navigate([ROUTES_CONSTANTS.RAPIFLEX_NOW]);
      } else if (alternativeProductName === ProductType.RETRP) {
        this.router.navigate([ROUTES_CONSTANTS.REFINANCE_LOAN]);
      }
    });
  }

  private checkInsuranceNotification(): void {
    this.insuranceNotificationService.checkAndShowInsurancePopup(this.clientId, this.dialog);
  }

  private redirectToReferrals(): void {
    this.ngZone.run(() => {
      this.router.navigate([ROUTES_CONSTANTS.REFERRALS]);
    });
  }
  private redirectToProductCampaign(productCode: string): void {
    if (productCode === ProductType.RF) {
      this.redirectToRF();
    }
  }

  private redirectToRF(): void {
    this.cookieService.set(COOKIE_ENUMERATION.CRFRAP, this.authService.token, 1, '/', environment.domain);
    window.location.href = environment.myCreditsRapiflex;
  }
}
